import styled from 'styled-components';

export type TransferProgressStepStatus = 'complete' | 'active' | 'not started';

// 3-row grid: top connector stub (half icon height), icon row, bottom connector + content.
// Connectors use negative margins to bridge the 8px gap between steps in the parent.
export const StepWrapper = styled.div`
  display: grid;
  grid-template-columns: 20px 1fr;
  grid-template-rows: 10px auto auto;
  column-gap: 24px;
  padding-left: 12px;
`;

// Left column spans all 3 rows, uses subgrid so rows match StepWrapper.
export const StepIndicatorColumn = styled.div`
  grid-column: 1;
  grid-row: 1 / 4;
  display: grid;
  grid-template-rows: subgrid;
  justify-items: center;
`;

// Row 1: top connector stub. Colored by current step status (not prev).
// margin-bottom: -16px pulls it into the icon row so it meets the icon center.
// margin-top: -4px extends it 4px up to bridge half the 8px gap between steps.
export const StepConnectorTop = styled.div<{
  $hasPrev: boolean;
  $status: TransferProgressStepStatus;
}>`
  width: 2px;
  align-self: stretch;
  justify-self: center;
  margin-top: ${({ $hasPrev }) => ($hasPrev ? '-4px' : '0')};
  margin-bottom: -16px;
  background-color: ${({ theme, $hasPrev, $status }) => {
    if (!$hasPrev) return 'transparent';
    if ($status === 'not started') return theme.colors.strokePrimaryGrey20;
    return theme.colors.buttonButtonPrimarySecondary2100;
  }};
`;

// Row 2: icon. position: relative + z-index so it renders above the connector lines.
export const StepIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
`;

export const StepCircle = styled.div<{ $status: 'active' | 'complete' | 'not started' }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid
    ${({ theme, $status }) =>
      ['active', 'complete'].includes($status)
        ? theme.colors.buttonButtonPrimarySecondary2100
        : theme.colors.strokePrimaryGrey20};
  background-color: ${({ theme }) => theme.colors.backgroundPrimaryWhite};

  svg {
    color: ${({ theme }) => theme.colors.buttonButtonPrimarySecondary2100};
    flex-shrink: 0;
    display: block;
    width: 8px;
    height: 8px;
  }
`;

// Row 3: bottom connector. margin-top: -16px pulls it up into the icon row.
// margin-bottom: -4px extends it 4px down to bridge half the 8px gap between steps.
export const StepConnector = styled.div<{ $nextStatus: TransferProgressStepStatus | null }>`
  width: 2px;
  align-self: stretch;
  justify-self: center;
  margin-top: -16px;
  margin-bottom: ${({ $nextStatus }) => ($nextStatus !== null ? '-4px' : '0')};
  background-color: ${({ theme, $nextStatus }) => {
    if ($nextStatus === null) return 'transparent';
    if ($nextStatus === 'not started') return theme.colors.strokePrimaryGrey20;
    return theme.colors.buttonButtonPrimarySecondary2100;
  }};
`;

// Right column spans rows 2–3.
export const StepRightColumn = styled.div`
  grid-column: 2;
  grid-row: 2 / 4;
  display: grid;
  grid-template-rows: subgrid;
`;

// Drives row 2 height.
export const StepTitleGroup = styled.div`
  min-height: 20px;
`;

export const StepTitle = styled.div<{ $status: TransferProgressStepStatus }>`
  font-size: 14px;
  font-weight: ${({ $status }) => ($status === 'not started' ? 400 : 700)};
  line-height: 24px;
  color: ${({ theme, $status }) =>
    $status === 'not started'
      ? theme.colors.textSystemDisabledGrey50
      : theme.colors.textPrimarySecondary1100};
`;

export const StepTitleLink = styled.a`
  font-size: 16px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.textSecondaryGrey90};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    text-decoration: underline;
  }
`;

export const StepStatusText = styled.div<{ $status: TransferProgressStepStatus }>`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${({ theme, $status }) =>
    $status === 'not started'
      ? theme.colors.textSystemDisabledGrey50
      : theme.colors.textSecondaryGrey90};
  margin-top: 4px;
`;

// Drives row 3 height — connector spans the same height alongside it.
export const StepBulletList = styled.ul<{ $status: TransferProgressStepStatus }>`
  list-style: disc;
  padding-left: 20px;
  margin: 0;
  li {
    font-size: 14px;
    line-height: 20px;
    color: ${({ theme, $status }) =>
      $status === 'not started'
        ? theme.colors.textSystemDisabledGrey50
        : theme.colors.textSecondaryGrey90};
    margin-top: 8px;
  }
`;
